import React, { useEffect, useState } from "react";
import api, { formatINR } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Save, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploadInput from "./ImageUploadInput";

const blankVariant = (sizeId = "") => ({
    size_id: sizeId,
    size_label: "",
    price: 0,
    distributor_price: null,
    stock: 0,
    sku: "",
    hsn: "",
});

/**
 * Full-fat product editor — name / category / descriptions / per-size variants
 * (label, SKU, HSN, price, distributor price, stock) + add/remove a variant.
 * Used from Stock Register and any other admin page that wants inline product
 * edit without bouncing the user to `/admin/products`.
 */
export default function ProductEditor({ open, onOpenChange, productId, onSaved }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [product, setProduct] = useState(null);
    const [cats, setCats] = useState([]);
    const [sizes, setSizes] = useState([]);

    useEffect(() => {
        if (!open || !productId) return;
        (async () => {
            setLoading(true);
            try {
                const [pr, cr, sr] = await Promise.all([
                    api.get(`/products/${productId}`),
                    api.get("/categories"),
                    api.get("/sizes"),
                ]);
                setProduct(pr.data);
                setCats(cr.data);
                setSizes(sr.data);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Couldn't load product");
                onOpenChange(false);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, productId, onOpenChange]);

    const set = (k, v) => setProduct((p) => ({ ...p, [k]: v }));
    const setVar = (i, k, v) => setProduct((p) => {
        const list = [...p.variants];
        list[i] = { ...list[i], [k]: v };
        return { ...p, variants: list };
    });
    const addVariant = () => setProduct((p) => ({ ...p, variants: [...(p.variants || []), blankVariant()] }));
    const removeVariant = (i) => setProduct((p) => {
        const list = [...p.variants];
        list.splice(i, 1);
        return { ...p, variants: list };
    });

    const save = async () => {
        if (!product.name?.trim()) return toast.error("Product name is required");
        if (!product.category_id) return toast.error("Category is required");
        if (!product.variants?.length) return toast.error("At least one size variant is required");
        for (let i = 0; i < product.variants.length; i++) {
            const v = product.variants[i];
            if (!v.size_id) return toast.error(`Variant #${i + 1}: pick a size`);
            if (Number.isNaN(parseFloat(v.price)) || parseFloat(v.price) < 0) return toast.error(`Variant #${i + 1}: invalid price`);
        }
        // Auto-fill size_label from the picked size (so saved row matches catalog)
        const sizeMap = Object.fromEntries(sizes.map((s) => [s.id, s.label]));
        const payload = {
            name: product.name,
            category_id: product.category_id,
            short_description: product.short_description || "",
            description: product.description || "",
            indications: product.indications || "",
            composition: product.composition || "",
            dosage: product.dosage || "",
            images: product.images || [],
            active: !!product.active,
            featured: !!product.featured,
            variants: product.variants.map((v) => ({
                size_id: v.size_id,
                size_label: sizeMap[v.size_id] || v.size_label || "",
                price: parseFloat(v.price) || 0,
                distributor_price: v.distributor_price === "" || v.distributor_price === null || v.distributor_price === undefined
                    ? null : parseFloat(v.distributor_price),
                stock: parseInt(v.stock, 10) || 0,
                sku: v.sku || null,
                hsn: v.hsn || null,
            })),
        };

        setSaving(true);
        try {
            await api.put(`/products/${productId}`, payload);
            toast.success(`'${payload.name}' saved`);
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save product");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-3xl max-h-[88vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Edit product</DialogTitle>
                    <DialogDescription>
                        Update everything about this product — name, category, descriptions, and each size variant. All changes are activity-logged.
                    </DialogDescription>
                </DialogHeader>

                {loading || !product ? (
                    <div className="py-12 grid place-items-center text-stone-500">
                        <Loader2 className="w-5 h-5 animate-spin" /><span className="mt-2 text-sm">Loading…</span>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {/* Product-level fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Product name" required>
                                <Input value={product.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-none border-stone-300" data-testid="pe-name" />
                            </Field>
                            <Field label="Category" required>
                                <Select value={product.category_id} onValueChange={(v) => set("category_id", v)}>
                                    <SelectTrigger className="rounded-none border-stone-300" data-testid="pe-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </Field>
                        </div>

                        <Field label="Short description">
                            <Input value={product.short_description || ""} onChange={(e) => set("short_description", e.target.value)} className="rounded-none border-stone-300" data-testid="pe-short" />
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Indications (symptoms)"><Textarea rows={2} value={product.indications || ""} onChange={(e) => set("indications", e.target.value)} className="rounded-none border-stone-300" data-testid="pe-indications" /></Field>
                            <Field label="Dosage / Posology"><Textarea rows={2} value={product.dosage || ""} onChange={(e) => set("dosage", e.target.value)} className="rounded-none border-stone-300" data-testid="pe-dosage" /></Field>
                        </div>

                        <Field label="Composition">
                            <Input value={product.composition || ""} onChange={(e) => set("composition", e.target.value)} className="rounded-none border-stone-300" data-testid="pe-composition" />
                        </Field>

                        <Field label="Long description">
                            <Textarea rows={3} value={product.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-none border-stone-300" data-testid="pe-long" />
                        </Field>

                        <div className="flex gap-6 pt-1">
                            <Toggle label="Active (visible in catalog)" checked={product.active} onCheckedChange={(v) => set("active", v)} testid="pe-active" />
                            <Toggle label="Featured" checked={product.featured} onCheckedChange={(v) => set("featured", v)} testid="pe-featured" />
                        </div>

                        {/* Images */}
                        <div className="border-t border-stone-200 pt-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-serif text-lg text-slate-900">Product images</h3>
                                <span className="text-[10px] uppercase tracking-widest text-stone-500">{(product.images || []).length} uploaded · first one becomes the cover</span>
                            </div>
                            <ImageUploadInput
                                value=""
                                onChange={(url) => { if (url) set("images", [...(product.images || []), url]); }}
                                testId="pe-img"
                                multiple={true}
                                hint="Pick one or many — every product photo, label, dosage card. ≤ 6 MB each. They all show in the customer-facing gallery."
                            />
                            {(product.images || []).length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3" data-testid="pe-images-list">
                                    {(product.images || []).map((url, i) => (
                                        <div key={i} className="relative w-20 h-20 border border-stone-200 bg-stone-100 group" data-testid={`pe-img-${i}`}>
                                            <img src={url} alt="" className="w-full h-full object-cover" />
                                            {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-sky-700 text-white text-[9px] text-center uppercase tracking-widest py-0.5">Cover</span>}
                                            <button
                                                onClick={() => set("images", (product.images || []).filter((_, j) => j !== i))}
                                                className="absolute -top-2 -right-2 w-5 h-5 bg-stone-900 text-stone-50 grid place-items-center hover:bg-red-700"
                                                data-testid={`pe-img-remove-${i}`}
                                                title="Remove image"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Variants */}
                        <div className="border-t border-stone-200 pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-serif text-lg text-slate-900">Size variants</h3>
                                <Button onClick={addVariant} variant="outline" className="rounded-none border-sky-700 text-sky-700 h-8" data-testid="pe-add-variant">
                                    <Plus className="w-3.5 h-3.5 mr-1" />Add variant
                                </Button>
                            </div>
                            {(!product.variants?.length) && (
                                <p className="text-sm text-stone-500 italic">No variants. Add at least one size.</p>
                            )}
                            <div className="space-y-2">
                                {product.variants.map((v, i) => (
                                    <div key={i} className="bg-sky-50/40 border border-sky-100 p-3 grid grid-cols-12 gap-2 items-end" data-testid={`pe-variant-${i}`}>
                                        <div className="col-span-12 sm:col-span-3">
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Size</Label>
                                            <Select value={v.size_id} onValueChange={(val) => setVar(i, "size_id", val)}>
                                                <SelectTrigger className="rounded-none border-stone-300 h-9" data-testid={`pe-var-size-${i}`}><SelectValue placeholder="Pick size" /></SelectTrigger>
                                                <SelectContent>{sizes.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Price ₹</Label>
                                            <Input type="number" min={0} step="0.01" value={v.price ?? 0} onChange={(e) => setVar(i, "price", e.target.value)} className="rounded-none border-stone-300 h-9" data-testid={`pe-var-price-${i}`} />
                                        </div>
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Distributor ₹</Label>
                                            <Input type="number" min={0} step="0.01" value={v.distributor_price ?? ""} onChange={(e) => setVar(i, "distributor_price", e.target.value)} placeholder="fallback price" className="rounded-none border-stone-300 h-9" data-testid={`pe-var-dist-${i}`} />
                                        </div>
                                        <div className="col-span-4 sm:col-span-1">
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Stock</Label>
                                            <Input type="number" min={0} value={v.stock ?? 0} onChange={(e) => setVar(i, "stock", e.target.value)} className="rounded-none border-stone-300 h-9" data-testid={`pe-var-stock-${i}`} />
                                        </div>
                                        <div className="col-span-4 sm:col-span-2">
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">SKU</Label>
                                            <Input value={v.sku || ""} onChange={(e) => setVar(i, "sku", e.target.value)} className="rounded-none border-stone-300 h-9 font-mono text-xs" data-testid={`pe-var-sku-${i}`} />
                                        </div>
                                        <div className="col-span-3 sm:col-span-1">
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">HSN</Label>
                                            <Input value={v.hsn || ""} onChange={(e) => setVar(i, "hsn", e.target.value)} className="rounded-none border-stone-300 h-9 font-mono text-xs" data-testid={`pe-var-hsn-${i}`} />
                                        </div>
                                        <div className="col-span-1 flex justify-end">
                                            <button
                                                onClick={() => removeVariant(i)}
                                                className="text-red-700 hover:bg-red-50 border border-red-300 p-1.5 h-9 grid place-items-center"
                                                data-testid={`pe-var-remove-${i}`}
                                                title="Remove this variant"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        {v.distributor_price === "" || v.distributor_price === null || v.distributor_price === undefined ? (
                                            <div className="col-span-12 text-[11px] text-stone-500">
                                                <span className="font-mono">Distributors will be charged {formatINR(parseFloat(v.price) || 0)}</span> (fallback to retail price).
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter className="border-t border-stone-200 pt-3">
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="pe-cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving || loading} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="pe-save">
                        <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save product"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const Field = ({ label, required, children }) => (
    <div>
        <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}{required && <span className="text-red-700 ml-0.5">*</span>}</Label>
        <div className="mt-1">{children}</div>
    </div>
);

const Toggle = ({ label, checked, onCheckedChange, testid }) => (
    <label className="flex items-center gap-2 cursor-pointer text-sm">
        <Switch checked={!!checked} onCheckedChange={onCheckedChange} data-testid={testid} />
        <span>{label}</span>
    </label>
);
